import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AuthError } from "aws-amplify/auth";
import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { cognitoErrorMessages, cognitoErrorNames, useAuth } from "../hooks/useAuth";
import { type InitPWFormValue, initPWSchema } from "../schemas";

export const InitPW = (): React.ReactElement => {
	const { handleConfirmSignIn, currentAuthenticatedUser } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const email = searchParams.get("email") || "";

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
	} = useForm<InitPWFormValue>({
		resolver: zodResolver(initPWSchema),
	});

	const onSubmit = async (data: InitPWFormValue) => {
		clearErrors("root");
		setIsLoading(true);
		try {
			const result = await handleConfirmSignIn({
				username: email,
				...data,
			});
			if (result.isSignedIn) {
				const response = await currentAuthenticatedUser();
				response?.isAuthenticated ? navigate("/") : alert(response);
			} else {
				alert(result);
			}
		} catch (err) {
			if (err instanceof AuthError) {
				switch (err.name) {
					case cognitoErrorNames.ConfirmSignIn.UserNotFound:
						alert("メールアドレスが正しくありません。");
						break;
					case cognitoErrorNames.ConfirmSignIn.NotAuthorized:
						switch (err.message) {
							case cognitoErrorMessages.ConfirmSignIn.UserDisabled:
								setError("tempPassword", {
									message: "無効なユーザーです。",
								});
								break;
							case cognitoErrorMessages.ConfirmSignIn.Incorrect:
								setError("tempPassword", {
									message: "メールアドレスまたは仮パスワードが正しくありません。",
								});
								break;
						}
						break;
				}
			} else if (err instanceof Error) {
				// New password and new password confirmation mismatch check
				switch (err.name) {
					case cognitoErrorNames.ConfirmSignIn.PasswordMismatch:
						setError("newConfirmPassword", {
							message: err.message,
						});
						break;
					default:
						alert(err.message);
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				border: "1px solid #d0d7de",
				borderRadius: 1,
				padding: 2,
				backgroundColor: "#f5f5f5",
			}}
		>
			<Typography component="h2" variant="h5" sx={{ mb: 2 }}>
				初回パスワード設定
			</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				sx={{
					width: "100%",
					mt: 1,
				}}
			>
				<Typography variant="h6" sx={{ wordBreak: "break-word", textAlign: "center" }}>
					{email}
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					autoComplete="current-password"
					label={"仮パスワード"}
					type="password"
					sx={{ backgroundColor: "white" }}
					{...register("tempPassword")}
					error={Boolean(errors.tempPassword)}
				/>
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{errors.tempPassword?.message}
				</Typography>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						mt: 2,
					}}
				>
					パスワードは「数字」「特殊文字」「大文字」「小文字」を含む8文字以上で設定してください。
				</Typography>

				<TextField
					margin="normal"
					required
					fullWidth
					label={"新しいパスワード"}
					type="password"
					autoComplete="new-password"
					sx={{ backgroundColor: "white" }}
					{...register("newPassword")}
					error={Boolean(errors.newPassword)}
				/>
				<Typography variant="caption" color="error">
					{errors.newPassword?.message}
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					label={"新しいパスワードの確認"}
					type="password"
					autoComplete="new-password"
					sx={{ backgroundColor: "white" }}
					{...register("newConfirmPassword")}
					error={Boolean(errors.newConfirmPassword)}
				/>
				<Typography variant="caption" color="error">
					{errors.newConfirmPassword?.message}
				</Typography>
				<LoadingButton
					type="submit"
					fullWidth
					variant="contained"
					loading={isLoading}
					sx={{ mt: 3, mb: 2, fontWeight: "bold" }}
				>
					パスワードを設定
				</LoadingButton>
			</Box>
		</Box>
	);
};
